import { ListboxOption, ListboxOptions } from '@headlessui/react'
import { AnchorPropsWithSelection } from '@headlessui/react/dist/internal/floating'
import { SearchIcon } from '@heroicons/react/outline'
import { CheckIcon } from '@heroicons/react/solid'
import { c } from 'lib/component-utils'
import { DisplayExtractor, extract, KeyExtractor } from 'lib/type-utils'
import { ForwardedRef, forwardRef } from 'react'
import { useIntl } from 'lib/intl-utils'

type Props<T> = {
  options: T[]
  optionKey?: KeyExtractor<T>
  display?: DisplayExtractor<T>
  className?: string
  anchor?: AnchorPropsWithSelection
  searchable?: boolean
  searchQuery?: string
  onSearchQueryChange?: (query: string) => void
  searchPlaceholder?: string
  searchInputRef?: React.RefObject<HTMLInputElement | null>
}

const SelectDropdown = <T,>(
  {
    options,
    optionKey,
    display,
    className,
    anchor,
    searchable,
    searchQuery = '',
    onSearchQueryChange,
    searchPlaceholder,
    searchInputRef,
  }: Props<T>,
  ref: ForwardedRef<HTMLUListElement>
) => {
  const { t } = useIntl()
  return (
    <ListboxOptions
      ref={ref}
      modal={false}
      anchor={{ to: 'bottom start', gap: 8, padding: 16, ...((anchor as any) || {}) }}
      className={c`bg-white !max-h-64 h-fit overscroll-contain ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-20 w-64 overflow-auto text-base rounded-lg shadow-lg ${className}`}
    >
      {searchable && (
        <div
          className="sticky top-0 z-10 bg-white border-b border-gray-100"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <label className="flex h-10 py-2 bg-white ring-1 ring-gray-200">
            <div className="relative flex items-center w-auto pl-4 pr-2 mr-px -ml-px text-gray-400 shrink-0">
              <SearchIcon className="w-4 h-4" />
            </div>
            <input
              ref={searchInputRef as React.RefObject<HTMLInputElement>}
              type="text"
              value={searchQuery}
              onChange={(event) => onSearchQueryChange?.(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              autoComplete="off"
              placeholder={searchPlaceholder}
              className="block w-full px-0 py-2 text-sm text-black border-none rounded-md focus:border-none focus:outline-none focus:ring-0 placeholder:text-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
            />
          </label>
        </div>
      )}
      {searchable && options.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500">{t`select.no_results`}</div>
      ) : (
        options.map((option) => (
          <ListboxOption
            key={extract(option, optionKey)}
            className={({ focus }) =>
              c`cursor-pointer select-none relative py-2.5 pl-8 pr-4 text-left ${focus} text-black bg-gray-100 | text-gray-900`
            }
            value={option}
          >
            {({ selected, focus }) => (
              <>
                <span className={c`block truncate ${selected} font-medium`}>
                  {extract(option, display)}
                </span>
                {selected && (
                  <span
                    className={c`absolute inset-y-0 left-0 flex items-center pl-3 z-10 text-primary-500 ${focus} !text-primary-600`}
                  >
                    <CheckIcon className="w-4 h-4" aria-hidden="true" />
                  </span>
                )}
              </>
            )}
          </ListboxOption>
        ))
      )}
    </ListboxOptions>
  )
}

export default forwardRef<HTMLUListElement, {}>(SelectDropdown as any) as <T>(
  { options, optionKey, display, className }: Props<T>,
  ref: ForwardedRef<HTMLUListElement>
) => JSX.Element
